/**
 * TaskDrawerHeader Component
 *
 * Renders the header section of the task drawer including:
 * - Task title with urgency badge
 * - Project link tag (if applicable)
 * - Status selector
 * - Copy link and edit buttons
 *
 * Accessibility:
 * - Proper heading hierarchy (h2 for drawer title)
 * - ARIA labels on interactive elements
 * - Focus management for status selector
 *
 * @see WCAG 2.1 AA - 1.3.1 Info and Relationships
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import {
  LinkOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import '../../styles/index.scss';

import { urgencyHexColors } from '../../configs/task.config';
import { border } from '../../configs/designTokens';

const TaskDrawerHeader = ({
  currentTask,
  taskId,
}) => {
  const location = useLocation();
  const dotColor = urgencyHexColors[currentTask.urgency] || border.disabled;

  return (
    <div
      className="task-drawer-full__header"
      role="banner"
      aria-label="Task header"
    >
      <div className="task-drawer-full__header-row">
        <div className="task-drawer-full__title-area">
          <span
            className="task-drawer-full__header-dot"
            style={{ backgroundColor: dotColor }}
            aria-hidden="true"
          />
          <div className="task-drawer-full__title-content">
            <Text
              strong
              className="task-drawer-full__title"
              id="task-drawer-title"
            >
              {currentTask.name}
            </Text>
            {currentTask.project && (
              <Link
                to={`/tasks/projects/details/${currentTask.project.id}`}
                aria-label={`View project: ${currentTask.project.name}`}
                className="task-drawer-full__project-link"
              >
                <span className="task-drawer-full__project-tag">
                  <LinkOutlined
                    className="task-drawer-full__icon-inline"
                    aria-hidden="true"
                  />
                  {currentTask.project.name}
                </span>
              </Link>
            )}
          </div>
          {currentTask.urgency && (
            <span className="sr-only">
              Urgency: {currentTask.urgency.toLowerCase()}
            </span>
          )}
        </div>

        <Link
          to={{
            pathname: `/tasks/details/${taskId}`,
            state: { from: `${location.pathname}${location.search}` },
          }}
        >
          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            className="task-drawer-full__view-btn"
            aria-label="View full task page"
          >
            View Full Page
          </Button>
        </Link>
      </div>
    </div>
  );
};

TaskDrawerHeader.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    urgency: PropTypes.string,
    project: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  }).isRequired,
  taskId: PropTypes.string.isRequired,
};

export default TaskDrawerHeader;
